import { useEffect, useRef } from 'react';

import { useSipCallStore } from './sipCallStore';

/**
 * Plays a simple ringtone while there is an incoming or active SIP call.
 * Stops when there is no call.
 *
 * Note: browsers may block autoplay without user interaction.
 */
export function useSipRingtone(): void {
  // Play only while ringing (pending invitation). Stop once answered.
  const isRinging = useSipCallStore(s => !!s.pendingInvitation);
  const audioRef = useRef<{
    ctx: AudioContext;
    osc: OscillatorNode;
    gain: GainNode;
    intervalId: number;
    timeoutId: number;
  } | null>(null);

  useEffect(() => {
    if (!isRinging) {
      if (audioRef.current) {
        const { osc, ctx, intervalId, timeoutId } = audioRef.current;
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
        try {
          osc.stop();
        } catch {
          // ignore stop errors
        }
        ctx.close().catch(() => {});
        audioRef.current = null;
      }
      return;
    }

    if (audioRef.current) return;

    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      // Two-tone ring: alternate 440Hz and 480Hz with short cadence
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.value = 0.08;

      osc.connect(gain);
      gain.connect(ctx.destination);

      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      osc.start();

      // Ring cadence: 0.8s on (alternating), 0.4s off, repeat
      let toneHigh = false;
      const intervalId = window.setInterval(() => {
        toneHigh = !toneHigh;
        osc.frequency.value = toneHigh ? 480 : 440;
      }, 200);

      const cycle = () => {
        gain.gain.value = 0.08;
        audioRef.current!.timeoutId = window.setTimeout(() => {
          gain.gain.value = 0;
          audioRef.current!.timeoutId = window.setTimeout(cycle, 400);
        }, 800);
      };
      const timeoutId = window.setTimeout(cycle, 0);

      audioRef.current = { ctx, osc, gain, intervalId, timeoutId };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[WebRTC] Ringtone failed to start (autoplay blocked or no audio device)', err);
    }
  }, [isRinging]);
}
